import { useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Link, useParams } from "react-router-dom";

import PageLayout from "@/components/PageLayout";
import PageHeader from "@/components/PageHeader";
import ProjectAccessDenied from "@/components/ProjectAccessDenied";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import DocumentUploadForm from "@/components/DocumentUploadForm";
import { toast } from "sonner";
import { EvidenceBase } from "@lumanity/ui/evidence";
import { useOpenEvidenceDocument, useRefreshDocuments } from "@/lib/evidence";
import * as DocumentToolbar from "@/components/DocumentToolbar";
import UnsavedChangesDialog from "@/components/UnsavedChangesDialog";
import { useProject, useProjectDocuments, useUpdateProject } from "@/hooks/useProjects";
import { useProjectAccess } from "@/hooks/useProjectAccess";
import { useProjectCustomFields } from "@/hooks/useProjectCustomFields";
import { useDocuments } from "@/hooks/useDocumentMetadata";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { PROJECT_STATUS } from "@/config/projectStatus";

const ProjectFiles = () => {
  const { id = "" } = useParams<{ id: string }>();
  const { data: project, isLoading: projectLoading } = useProject(id);
  const { data: customFields = [], isLoading: fieldsLoading } = useProjectCustomFields(id);
  const {
    data: projectDocuments = [],
    isLoading: projectDocsLoading,
    refetch: refetchProjectDocuments,
  } = useProjectDocuments(id);
  const { data: allDocuments = [] } = useDocuments();
  const { hasAccess, isLoading: accessLoading } = useProjectAccess(project);
  const updateProject = useUpdateProject();
  const openDocument = useOpenEvidenceDocument();
  const refreshDocuments = useRefreshDocuments();

  const [forceUploadMode, setForceUploadMode] = useState(false);
  const [uploadDirty, setUploadDirty] = useState(false);
  const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesGuard(uploadDirty);

  const activeDocIds = useMemo(
    () => new Set(projectDocuments.filter((p) => p.isActive).map((p) => p.documentId)),
    [projectDocuments],
  );
  const documents = useMemo(
    () => allDocuments.filter((d) => activeDocIds.has(d.id)),
    [allDocuments, activeDocIds],
  );

  const showUpload =
    forceUploadMode ||
    uploadDirty ||
    project?.status === PROJECT_STATUS.DocumentUpload ||
    (!projectDocsLoading && documents.length === 0);

  const handleUploadComplete = async () => {
    setForceUploadMode(false);
    await refetchProjectDocuments();
    if (project?.status === PROJECT_STATUS.DocumentUpload && id) {
      try {
        await updateProject.mutateAsync({ id, status: PROJECT_STATUS.EvidenceBase });
      } catch {
        /* non-blocking */
      }
    }
  };


  if (projectLoading || accessLoading) {
    return (
      <PageLayout>
        <div className="text-muted-foreground py-12 text-center">Loading project…</div>
      </PageLayout>
    );
  }

  if (!project) {
    return (
      <PageLayout>
        <div className="py-12 text-center space-y-4">
          <p className="text-muted-foreground">Project not found.</p>
          <Button asChild variant="outline">
            <Link to="/projects">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Projects
            </Link>
          </Button>
        </div>
      </PageLayout>
    );
  }

  if (!hasAccess) {
    return <ProjectAccessDenied projectId={project.id} />;
  }

  return (
    <PageLayout fullHeight>
      <div className="mb-4">
        <Button asChild variant="ghost" size="sm" className="gap-1 -ml-2">
          <Link to={`/projects/${project.id}`}>
            <ArrowLeft className="w-4 h-4" /> Back to Project
          </Link>
        </Button>
      </div>

      <PageHeader
        title={project.name}
        subtitle="Upload sources, review extracted metadata, and browse the project evidence base."
      />

      <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mb-6">
        <span className="font-medium text-foreground">{project.code}</span>
        <StatusBadge status={project.status} />
      </div>

      <UnsavedChangesDialog open={isBlocked} onConfirm={confirmLeave} onCancel={cancelLeave} />

      {showUpload || fieldsLoading ? (
        <div className="bg-card border rounded-lg p-6">
          <DocumentUploadForm
            projectId={id}
            projectModel={project.model}
            customFields={customFields}
            onComplete={handleUploadComplete}
            lockedOwnership="Private"
            onDirtyChange={setUploadDirty}
          />
        </div>
      ) : (
        <EvidenceBase
          fillHeight
          projectId={id}
          projectModel={project.model}
          isAdvancedRag={project.isAdvancedRag}
          onOpenDocument={openDocument}
          onSaved={() => refreshDocuments(id)}
          onDeleted={() => refreshDocuments(id)}
          onError={(e) => toast.error(e.message)}
          toolbarActions={
            <DocumentToolbar.UploadButton onClick={() => setForceUploadMode(true)} />
          }
        />
      )}
    </PageLayout>
  );
};

export default ProjectFiles;
